// components/ChatHistory.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { chatAPI } from "../../services/api";
import { formatDistanceToNow, format } from "date-fns";
import {
  ChatBubbleLeftIcon,
  TrashIcon,
  FolderIcon,
  PlusIcon,
  DocumentIcon,
  ChevronRightIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  NoSymbolIcon,
  ClockIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

const ChatHistory = ({
  currentSessionId,
  onNewSession,
  onSelectChat,
  isOpen = true,
  onClose, 
  chatload,
}) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Load sessions
  useEffect(() => {
    loadSessions();
  }, [chatload]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getHistory();
      
      if (response?.data?.success) {
        // Process sessions to add computed properties
        const processedSessions = response.data.messages.map(session => ({
          ...session,
          lastQuestion: getLastQuestion(session),
          lastResponse: getLastResponse(session),
          previewText: getMessagePreview(session),
          messageCount: session.messages?.length || 0,
          hasFile: session.messages?.some(msg => msg.hasFile) || false,
        }));
        setSessions(processedSessions);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error("Failed to load sessions:", error);
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getMessagePreview = useCallback((session) => {
    if (session.preview && session.preview !== "New Chat") {
      return session.preview;
    }
    
    if (session.messages && session.messages.length > 0) {
      const lastUserMessage = [...session.messages]
        .reverse()
        .find(msg => msg.question);
      
      if (lastUserMessage?.question) {
        const preview = lastUserMessage.question;
        return preview.length > 60 ? preview.substring(0, 60) + "..." : preview;
      }
      
      const lastAssistant = [...session.messages]
        .reverse()
        .find(msg => msg.answer);
      
      if (lastAssistant?.answer) {
        const preview = lastAssistant.answer;
        return preview.length > 60 ? preview.substring(0, 60) + "..." : preview;
      }
    }
    
    return "New conversation";
  }, []);

  const getLastQuestion = useCallback((session) => {
    if (session.messages && session.messages.length > 0) {
      const lastUserMessage = [...session.messages]
        .reverse()
        .find(msg => msg.question);
      return lastUserMessage?.question || null;
    }
    return null;
  }, []);

  const getLastResponse = useCallback((session) => {
    if (session.messages && session.messages.length > 0) {
      const lastAssistant = [...session.messages]
        .reverse()
        .find(msg => msg.answer);
      return lastAssistant?.answer || null;
    }
    return null;
  }, []);

  // Delete session with animation
  const handleDelete = async (sessionId, e) => {
    e.stopPropagation();
    
    toast.custom((t) => (
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        className="bg-white rounded-xl shadow-2xl p-4 max-w-sm mx-auto border border-gray-100"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="bg-red-50 rounded-full p-2">
              <TrashIcon className="h-5 w-5 text-red-500" />
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">Delete conversation?</p>
            <p className="text-xs text-gray-500 mt-1">This action cannot be undone.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              setDeletingId(sessionId);
              try {
                await chatAPI.deleteSession(sessionId);
                toast.success("Conversation deleted", {
                  icon: "🗑️",
                  style: { background: '#10B981', color: '#fff' }
                });
                await loadSessions();
                if (currentSessionId === sessionId && onNewSession) {
                  onNewSession();
                }
              } catch (error) {
                console.log(error);
                toast.error("Failed to delete conversation");
              } finally {
                setDeletingId(null);
              }
            }}
            className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </motion.div>
    ), { duration: 5000 });
  };

  // Group sessions by date
  const groupByDate = useCallback((sessionsList) => {
    const groups = {};
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const thisWeek = new Date(Date.now() - 7 * 86400000);
    const thisMonth = new Date(Date.now() - 30 * 86400000);

    sessionsList.forEach((session) => {
      const date = new Date(session.updated_at);
      const dateString = date.toDateString();
      
      let key;
      if (dateString === today) {
        key = "Today";
      } else if (dateString === yesterday) {
        key = "Yesterday";
      } else if (date > thisWeek) {
        key = "This Week";
      } else if (date > thisMonth) {
        key = "This Month";
      } else {
        key = format(date, 'MMMM yyyy');
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(session);
    });

    return groups;
  }, []);

  // Filter sessions based on search
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    
    const query = searchQuery.toLowerCase();
    return sessions.filter(session => 
      session.lastQuestion?.toLowerCase().includes(query) ||
      session.lastResponse?.toLowerCase().includes(query) ||
      session.previewText?.toLowerCase().includes(query)
    );
  }, [sessions, searchQuery]);

  const grouped = groupByDate(filteredSessions);
  const groupedEntries = Object.entries(grouped);

  // Toggle group expansion
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
    
        newSet.add(groupName);
      
      return newSet;
    });
  };

  // Loading skeleton with animation
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-gradient-to-b from-gray-50 to-white">
        <div className="p-5 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div className="h-6 w-28 bg-gradient-to-r from-gray-200 to-gray-100 rounded-lg animate-pulse"></div>
            <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse"></div>
          </div>
        </div>
        <div className="flex-1 p-4 space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="p-3 rounded-xl">
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-100 rounded w-3/4 animate-pulse"></div>
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-100 rounded w-1/2 mt-2 animate-pulse"></div>
              <div className="h-2 bg-gradient-to-r from-gray-200 to-gray-100 rounded w-1/3 mt-2 animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div 
    
      className="h-full flex flex-col bg-gradient-to-b from-white to-gray-50 shadow-xl border-r border-gray-100 relative"
    >
      {/* Header */}
      <div className="p-5 border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-1.5 shadow-lg shadow-blue-200">
              <FolderIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 text-lg">Chat History</h2>
            
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 lg:hidden"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            )}
           
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
         
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <AnimatePresence mode="wait">
          {filteredSessions.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center h-full px-4 py-12 text-center"
            >
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-4 mb-4">
                {searchQuery ? (
                  <NoSymbolIcon className="h-12 w-12 text-gray-400" />
                ) : (
                  <ChatBubbleLeftEllipsisIcon className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <p className="text-sm font-semibold text-gray-600">
                {searchQuery ? "No matching conversations" : "No conversations yet"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {searchQuery ? "Try a different search term" : "Start a new chat to begin"}
              </p>
              {!searchQuery && (
                <button
                  onClick={onNewSession}
                  className="mt-4 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium rounded-xl hover:shadow-lg hover:shadow-blue-200 transition-all duration-200"
                >
                  New Conversation
                </button>
              )}
            </motion.div>
          ) : (
            <div className="p-3 space-y-5">
              {groupedEntries.map(([date, dateSessions]) => (
                <motion.div
                  key={date}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Date header with toggle */}
                  <button
                    className="flex items-center gap-2 mb-2 px-2 w-full group"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 text-gray-400" />
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {date}
                    </h3>
                    <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent"></div>
                    <ChevronRightIcon className={`
                      h-3.5 w-3.5 text-gray-400 transition-transform duration-200
                      ${expandedGroups.has(date) ? 'rotate-90' : ''}
                      group-hover:translate-x-0.5
                    `} />
                    <span className="text-xs text-gray-400">({dateSessions.length})</span>
                  </button>

                  {/* Sessions */}
                  <AnimatePresence>
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-1.5 overflow-hidden"
                      >
                        {dateSessions.map((session, index) => {
                          const isActive = currentSessionId === session.session_id;
                          const isDeleting = deletingId === session.session_id;
                          const isHovered = hoveredId === session.id;
                          
                          return (
                            <motion.div
                              key={session.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.03 }}
                              onClick={() => !isDeleting && onSelectChat(session)}
                              onMouseEnter={() => setHoveredId(session.id)}
                              onMouseLeave={() => setHoveredId(null)}
                              className={`
                                group relative rounded-xl cursor-pointer transition-all duration-200
                                ${isActive 
                                  ? 'bg-gradient-to-r from-blue-50 to-blue-100/50 shadow-md shadow-blue-100/50' 
                                  : 'hover:bg-gray-100/80'
                                }
                                ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}
                              `}
                            >
                              <div className="p-3 bg-white">
                                {/* Main Content */}
                                <div className="flex items-start gap-3">
                                  {/* Icon */}
                                  <div className="flex-shrink-0 mt-0.5">
                                    <div className={`
                                      rounded-lg p-1.5 transition-all duration-200
                                      ${isActive 
                                        ? 'bg-blue-500 text-white shadow-sm' 
                                        : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                                      }
                                    `}>
                                      <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
                                    </div>
                                  </div>

                                  {/* Content */}
                                  <div className="flex-1 min-w-0">
                                    {/* Preview Text */}
                                    <p className={`
                                      text-sm font-medium truncate
                                      ${isActive ? 'text-blue-700' : 'text-gray-800'}
                                    `}>
                                      {session.lastQuestion || session.previewText}
                                    </p>
                                    
                                    {/* Assistant Response Preview */}
                                    {session.lastResponse && session.lastQuestion && (
                                      <p className="text-xs text-gray-500 truncate mt-1.5 flex items-center gap-1">
                                        <SparklesIcon className="h-3 w-3 text-gray-400" />
                                        <span>
                                          {session.lastResponse.length > 70 
                                            ? session.lastResponse.substring(0, 70) + '...' 
                                            : session.lastResponse}
                                        </span>
                                      </p>
                                    )}
                                    
                                    {/* Metadata */}
                                    <div className="flex items-center gap-2 mt-2">
                                      {session.hasFile && (
                                        <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded-md">
                                          <DocumentIcon className="h-2.5 w-2.5 text-gray-500" />
                                          <span className="text-xs text-gray-500">File</span>
                                        </div>
                                      )}
                                      
                                      <div className="flex items-center gap-1">
                                        <ClockIcon className="h-2.5 w-2.5 text-gray-400" />
                                        <span className="text-xs text-gray-400">
                                          {formatDistanceToNow(new Date(session.updated_at), {
                                            addSuffix: true,
                                          }).replace('about ', '')}
                                        </span>
                                      </div>
                                      
                                      <span className="text-xs text-gray-300">•</span>
                                      
                                      <span className="text-xs text-gray-400">
                                        {session.messageCount} {session.messageCount === 1 ? 'msg' : 'msgs'}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Actions */}
                                  {/* <div className="flex items-center gap-1">
                                    <button
                                      onClick={(e) => handleDelete(session.session_id, e)}
                                      disabled={isDeleting}
                                      className={`
                                        p-1.5 rounded-lg transition-all duration-200
                                        ${(isHovered || isActive) && !isDeleting
                                          ? 'opacity-100 hover:bg-red-50' 
                                          : 'opacity-0 group-hover:opacity-100'
                                        }
                                        ${isDeleting ? 'cursor-not-allowed' : ''}
                                      `}
                                    >
                                      <TrashIcon className={`
                                        h-3.5 w-3.5 transition-colors
                                        ${isDeleting ? 'text-gray-300' : 'text-gray-400 hover:text-red-500'}
                                      `} />
                                    </button>
                                    
                                    <ChevronRightIcon className={`
                                      h-3.5 w-3.5 transition-all duration-200
                                      ${isActive ? 'text-blue-500 opacity-100' : 'text-gray-300'}
                                      ${isHovered ? 'opacity-100 translate-x-0.5' : 'opacity-0 group-hover:opacity-100'}
                                    `} />
                                  </div> */}
                                </div>
                              </div>

                              {/* Active Indicator */}
                              {isActive && (
                                <motion.div
                                  layoutId="activeIndicator"
                                  className="absolute left-0 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full"
                                />
                              )}
                            </motion.div>
                          );
                        })}
                      </motion.div>
                  
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
   

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 20px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default ChatHistory;